import {postAnalyticsEvent} from '../api/analytics'
import {useBehaviorFlow} from './useBehaviorFlow'

/**
 * DG P3「再行銷」半邊：把**受眾訊號**推進 dataLayer，讓 GTM 能據此建立 GA4 受眾。
 *
 * 為什麼是受眾訊號而不是一般事件：既有的 `share_click` / `submit_inquiry` /
 * `select_*` 回答的是「漏斗轉換率多少」。再行銷要回答的是另一個問題——
 * **「誰值得再找一次？」**：看了場地卻沒詢問的人、開始填表卻放棄的人、
 * 收藏了卻沒下一步的人、以及**搜了卻什麼都沒找到**的人（那是需求存在但供給不足，
 * 對 SG 供給招募也是訊號）。
 *
 * ## 這裡只做埋點，不做投放
 *
 * 護欄 27（暫緩大規模付費投放，解鎖＝成交資料可估 LTV/CAC）仍然有效，DG P4 也還
 * 擋著。所以這一層的產出是**受眾母體**：等閘門開了，投放端不必再等 2–4 週累積資料。
 * 反過來說——**現在不埋，將來解鎖那天就是從零開始等**。
 *
 * ## 隱私：只送 id 與分類，不送任何 PII
 *
 * dataLayer 的內容會離開我們的伺服器進到 Google。所以這裡只放 space_id、分類、
 * 城市、價格級距這類**非個人**的維度；姓名、email、電話、訊息內文一律不進來。
 * 價格用級距而非實價，也是同一個理由（實價可用於指紋化）。
 */

type RemarketingEvent =
  | 'view_space'          // 看過場地詳情
  | 'begin_inquiry'       // 打開詢問表單（配合既有 submit_inquiry 可算放棄率）
  | 'add_to_favorites'    // 收藏
  | 'search_no_result'    // 搜尋無結果（需求存在、供給不足）
  | 'search_performed'    // BI P2:發生過一次搜尋（不論有沒有結果）
  | 'select_space'        // BI P2:搜尋結果列表點卡片

interface SpaceAudienceParams {
  spaceId: number | string
  category?: string
  city?: string
  hourlyPrice?: number
  /** BI P2:這次瀏覽是從搜尋結果點進來的,帶著同一次搜尋的請求識別碼。 */
  requestId?: string
}

/** BI P2:一次搜尋的結果摘要,見 behavior-event-catalog.md §5 search_performed。 */
interface SearchPerformedParams {
  filtersHash?: string
  cityIds?: number[]
  categoryIds?: Record<string, number[]>
  people?: number
  priceBand?: string
  resultCount: number
  requestId?: string
}

/** BI P2:搜尋結果列表點了哪張卡片,見 behavior-event-catalog.md §5 select_space。 */
interface SelectSpaceParams {
  spaceId: number | string
  requestId?: string
  listPosition?: number
}

/** 價格級距：實價會讓單一使用者可被指紋化，級距足夠分眾又不可逆推。 */
export function priceBand(price?: number): string | undefined {
  if (price === undefined || price === null || Number.isNaN(price)) return undefined
  if (price < 500) return 'lt_500'
  if (price < 1000) return '500_999'
  if (price < 2000) return '1000_1999'
  if (price < 5000) return '2000_4999'
  return 'gte_5000'
}

/**
 * 遞迴排序物件的 key,陣列保持原順序(順序本身是資料的一部分)。
 *
 * 為什麼不能只排序頂層 key 再丟給 JSON.stringify 當 replacer:replacer 傳陣列時,
 * 那份 key 清單會被拿去當**每一層**的允許清單——嵌套物件(如 category_ids 底下
 * 的分類 id)裡不在頂層清單上的 key 全部被丟棄,不同的巢狀篩選條件因此雜湊成同一個值。
 */
function sortKeysDeep(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(sortKeysDeep)
  if (value === null || typeof value !== 'object') return value

  const sorted: Record<string, unknown> = {}
  for (const key of Object.keys(value as Record<string, unknown>).sort()) {
    sorted[key] = sortKeysDeep((value as Record<string, unknown>)[key])
  }
  return sorted
}

/**
 * BI P2:篩選條件組合的穩定雜湊。零結果分析要知道「這是不是同一組篩選條件」,
 * 但關鍵字是自由文字,不能原樣送出——雜湊之後只是分組用的指紋,不可逆推回關鍵字。
 */
export function filtersHash(filterOptions: Record<string, unknown>): string {
  const json = JSON.stringify(sortKeysDeep(filterOptions))
  let hash = 0
  for (let i = 0; i < json.length; i++) {
    hash = (hash * 31 + json.charCodeAt(i)) | 0
  }
  return (hash >>> 0).toString(16)
}

function push(event: RemarketingEvent, payload: Record<string, unknown>): void {
  // SSR 下沒有 window。Nuxt 的元件會在伺服器端跑一次，少了這道就會整頁 500。
  if (typeof window === 'undefined') return

  const w = window as unknown as { dataLayer?: Record<string, unknown>[] }
  w.dataLayer = w.dataLayer || []
  // undefined 的欄位不送：GTM 端看到 key 存在但值為 undefined，會建出一個
  // 「值為字串 'undefined'」的受眾維度，比沒有這個 key 更難查。
  const clean = Object.fromEntries(
    Object.entries(payload).filter(([, v]) => v !== undefined && v !== null && v !== '')
  )
  w.dataLayer.push({ event, ...clean })
}

/** BI P2：四個事件同時服務 collector（第一方 MySQL）的白名單，見 behavior-event-catalog.md §5。 */
const COLLECTOR_EVENTS: ReadonlySet<RemarketingEvent> = new Set([
  'view_space',
  'search_performed',
  'search_no_result',
  'select_space',
])

/**
 * BI P2：與 push() 並行送 collector，不取代——dataLayer 服務 DG 再行銷，collector
 * 服務 BI，兩者是不同消費者（見 docs/behavior-insights/implementation-p5.md §1.1）。
 * `begin_inquiry`／`add_to_favorites` 不在白名單內，呼叫時安靜跳過。
 *
 * BI-UI v2 上線後，這四個事件改由 useBehaviorFlow 的對應 trackXxx 送 v2 collector；
 * 這裡的 v1 送法必須讓路，否則同一次瀏覽/搜尋會在 v1、v2 各留一筆（implementation.md
 * §4.2「不得 v1/v2 雙寫」）。v2 是否啟用由呼叫端（useSearchWithStore 等）決定是否呼叫
 * v2 的 trackXxx；這裡只需知道「v2 有在送」就把 v1 讓開。
 */
async function pushCollector(event: RemarketingEvent, payload: Record<string, unknown>): Promise<void> {
  // SSR 下沒有 window，同 push() 的理由：collector 呼叫必須是 client-only。
  if (typeof window === 'undefined') return
  if (!COLLECTOR_EVENTS.has(event)) return
  if (await useBehaviorFlow().isV2Enabled()) return

  const clean = Object.fromEntries(
    Object.entries(payload).filter(([, v]) => v !== undefined && v !== null && v !== '')
  )
  void postAnalyticsEvent(event, clean)
}

export function useRemarketing() {
  return {
    /** 場地詳情頁瀏覽。與 submit_inquiry 比對即得「看了沒問」的受眾。 */
    trackViewSpace(params: SpaceAudienceParams): void {
      const payload = {
        space_id: params.spaceId,
        space_category: params.category,
        space_city: params.city,
        price_band: priceBand(params.hourlyPrice),
        request_id: params.requestId,
      }
      push('view_space', payload)
      void pushCollector('view_space', payload)
    },

    /** BI P2:一次搜尋發生了(不論有沒有結果),回答「搜尋零結果」缺口。 */
    trackSearchPerformed(params: SearchPerformedParams): void {
      const payload = {
        filters_hash: params.filtersHash,
        city_ids: params.cityIds,
        category_ids: params.categoryIds,
        people: params.people,
        price_band: params.priceBand,
        result_count: params.resultCount,
        request_id: params.requestId,
      }
      push('search_performed', payload)
      void pushCollector('search_performed', payload)
    },

    /** BI P2:搜尋結果列表點了哪張卡片,回答「只看不詢問」缺口。 */
    trackSelectSpace(params: SelectSpaceParams): void {
      const payload = {
        space_id: params.spaceId,
        request_id: params.requestId,
        list_position: params.listPosition,
      }
      push('select_space', payload)
      void pushCollector('select_space', payload)
    },

    /** 詢問表單開啟。與 submit_inquiry 的差集＝放棄填表的人。 */
    trackBeginInquiry(params: { spaceId: number | string; broadcast?: boolean }): void {
      push('begin_inquiry', {
        space_id: params.spaceId,
        target_type: params.broadcast ? 'broadcast' : 'space',
      })
    },

    /** 收藏。高意圖但未必已詢問，是最值得再行銷的一群。 */
    trackAddToFavorites(params: SpaceAudienceParams): void {
      push('add_to_favorites', {
        space_id: params.spaceId,
        space_category: params.category,
        space_city: params.city,
        price_band: priceBand(params.hourlyPrice),
      })
    },

    /**
     * 搜尋無結果。這個訊號有兩個消費者：再行銷（需求未被滿足的人）
     * 與 SG 供給招募（哪些條件組合沒有供給）。**只送條件，不送原始查詢字串**——
     * 使用者可能在自由文字裡打進公司名或聯絡方式。
     */
    trackSearchNoResult(params: { city?: string; category?: string; people?: number }): void {
      const payload = {
        search_city: params.city,
        search_category: params.category,
        search_people: params.people,
      }
      push('search_no_result', payload)
      void pushCollector('search_no_result', payload)
    },
  }
}
